import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Users, Loader2, Plus, Check, AlertCircle, Split } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { canAssignPartners, canSplitCommission, canViewPartnerCommissionDetails, useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/utils-ros";

const DEFAULT_POOL = 30;

function buildSplitRows(current, partners, pool = DEFAULT_POOL) {
  const validIds = new Set(partners.map((p) => p.id));
  const active = current.filter((c) => c.status === "active");
  if (active.length) {
    return {
      pool: active[0]?.commission_pool_pct ?? pool,
      rows: active.map((c) => ({
        partner_id: validIds.has(c.partner_id) ? c.partner_id : partners[0]?.id || "",
        commission_pct: c.commission_pct,
        is_primary: c.is_primary,
      })),
    };
  }
  return {
    pool,
    rows: [{ partner_id: partners[0]?.id || "", commission_pct: pool, is_primary: true }],
  };
}

/**
 * PartnerAssignmentPanel — shows ascribed partner (visible to all roles in Universe).
 * Marketing assigns primary partner; admin splits commission pool (R-08).
 */
export function PartnerAssignmentPanel({ entityType, entityId, compact = false, accent = "copper", showActions = true }) {
  const { user } = useAuth();
  const navy = accent === "navy";
  const [assignments, setAssignments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booting, setBooting] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const canManage = canAssignPartners(user);
  const canSplit = canSplitCommission(user);
  const canViewPool = canViewPartnerCommissionDetails(user);

  const load = useCallback(async (opts = {}) => {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    try {
      const [{ data: a }, { data: p }] = await Promise.all([
        api.get(`/partner-assignments/${entityType}/${entityId}`),
        api.get("/partner-firms"),
      ]);
      setAssignments(Array.isArray(a) ? a : []);
      setPartners(Array.isArray(p) ? p : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load partner assignments");
    } finally {
      setLoading(false);
      setBooting(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    setBooting(true);
    load();
  }, [entityType, entityId, load]);

  async function openAssignModal() {
    try {
      const { data } = await api.get("/partner-firms");
      setPartners(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load partners");
      return;
    }
    setAssignOpen(true);
  }

  async function openSplitModal() {
    try {
      const { data } = await api.get("/partner-firms");
      setPartners(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load partners");
      return;
    }
    setSplitOpen(true);
  }

  const active = assignments.filter((a) => a.status === "active");
  const primary = active.find((a) => a.is_primary) || active[0];
  const poolPct = primary?.commission_pool_pct ?? active[0]?.commission_total_pct ?? DEFAULT_POOL;
  const totalShare = active.reduce((s, a) => s + Number(a.commission_pct || 0), 0);
  const hasSplit = active.length > 1;
  const canProposeTransfer = user?.role === "partner" && primary?.partner_id === user?.partner_id;

  if (compact) {
    return (
      <>
        <div
          className="flex items-center gap-2 min-w-0 min-h-[1.25rem]"
          data-testid="partner-assignment-compact"
          onClick={(e) => e.stopPropagation()}
        >
          {loading && booting ? (
            <span className="text-[10px] text-[var(--text-muted)] animate-pulse">Loading…</span>
          ) : primary ? (
            <span
              className={`text-[10px] truncate max-w-[140px] ${navy ? "text-[#07539A] font-medium" : "text-[var(--copper)]"}`}
              title={hasSplit ? `${primary.partner_name} (primary) · ${active.length} partners` : primary.partner_name}
            >
              {primary.partner_name}
              {hasSplit ? ` +${active.length - 1}` : ""}
            </span>
          ) : (
            <span className="text-[10px] text-[var(--text-dim)]">Unassigned</span>
          )}
          {showActions && canManage && (
            <button
              type="button"
              onClick={openAssignModal}
              data-testid="assign-partner-btn"
              className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${navy ? "bg-[#07539A] !text-white hover:bg-[#063F73]" : "bg-[var(--copper)]/10 text-[var(--copper)] hover:bg-[var(--copper)]/20"}`}
            >
              {primary ? "Reassign" : "Assign"}
            </button>
          )}
          {showActions && canSplit && primary && (
            <button
              type="button"
              onClick={openSplitModal}
              data-testid="split-commission-btn"
              className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${navy ? "bg-white border border-[#BBD8E7] text-[#07539A] hover:border-[#07539A]" : "bg-black/[0.04] text-[var(--text-muted)] hover:bg-black/[0.08]"}`}
            >
              Split
            </button>
          )}
          {showActions && canProposeTransfer && (
            <button type="button" onClick={() => setProposeOpen(true)} data-testid="propose-partner-btn"
              className="text-[10px] px-2 py-0.5 rounded-full shrink-0 bg-white border border-[#BBD8E7] text-[#07539A] hover:border-[#07539A]">
              Propose transfer
            </button>
          )}
        </div>
        {assignOpen && canManage && (
          <AssignModal
            entityType={entityType}
            entityId={entityId}
            partners={partners}
            onClose={() => { setAssignOpen(false); load({ silent: true }); }}
          />
        )}
        {splitOpen && canSplit && (
          <SplitModal
            entityType={entityType}
            entityId={entityId}
            partners={partners}
            current={active}
            onClose={() => { setSplitOpen(false); load({ silent: true }); }}
          />
        )}
        {proposeOpen && canProposeTransfer && (
          <ProposeTransferModal entityType={entityType} entityId={entityId} partners={partners} currentPartnerId={user.partner_id}
            onClose={() => setProposeOpen(false)} />
        )}
      </>
    );
  }

  return (
    <div className="border border-black/10 rounded-md" data-testid="partner-assignment">
      <div className={`px-3 py-2 border-b flex items-center gap-2 ${navy ? "border-[#D7E8F0] bg-[#E7F5FB]" : "border-black/10 bg-black/[0.02]"}`}>
        <Users size={13} className={navy ? "text-[#07539A]" : "text-[var(--copper)]"} />
        <span className={`text-[11px] uppercase tracking-widest ${navy ? "text-[#07539A] font-semibold" : ""}`}>Partner Attribution</span>
        <span className="text-[11px] font-mono text-[var(--text-dim)]">{active.length}</span>
        {canManage && (
          <button
            type="button"
            onClick={openAssignModal}
            data-testid="assign-partner-btn"
            className={`ml-auto text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 ${navy ? "bg-[#07539A] !text-white hover:bg-[#063F73]" : "bg-[var(--copper)]/10 text-[var(--copper)] hover:bg-[var(--copper)]/20"}`}
          >
            <Plus size={11} /> {active.length ? "Reassign" : "Assign"}
          </button>
        )}
        {canSplit && primary && (
          <button
            type="button"
            onClick={openSplitModal}
            data-testid="split-commission-btn"
            className={`text-[11px] px-2 py-0.5 rounded bg-black/[0.04] text-[var(--text-muted)] hover:bg-black/[0.08] flex items-center gap-1 ${canManage ? "" : "ml-auto"}`}
          >
            <Split size={11} /> Split commission
          </button>
        )}
        {canProposeTransfer && (
          <button type="button" onClick={() => setProposeOpen(true)} data-testid="propose-partner-btn"
            className="text-[11px] px-2.5 py-1 rounded-full border border-[#BBD8E7] text-[#07539A] hover:border-[#07539A]">
            Propose transfer
          </button>
        )}
      </div>
      <div className="p-3 min-h-[3.5rem]">
        {loading && booting ? <div className="text-[12px] text-[var(--text-muted)] animate-pulse">Loading…</div>
         : active.length === 0 ? (
          <div className="text-[12px] text-[var(--text-muted)]">
            No partner assigned. {canManage ? "Click Assign above." : "Ascribed partner will appear here once assigned."}
            {canManage && partners.length === 0 && !loading && (
              <div className="mt-2 text-[11px] text-[var(--warning)]">
                Create partner firms on the Partners page first — user accounts alone are not enough for assignment.
              </div>
            )}
          </div>
         ) : (
          <div className="space-y-2">
            {canViewPool && active.length > 0 && (
              <div className="text-[10px] text-[var(--text-dim)] mb-1">
                Commission pool: <span className={`font-mono ${navy ? "text-[#07539A]" : "text-[var(--copper)]"}`}>{poolPct}%</span> of payment
                {active.length > 1 && (
                  <>{" · "}allocated: <span className="font-mono">{totalShare}%</span></>
                )}
              </div>
            )}
            {active.map((a) => (
              <div key={a.id} className="flex items-center gap-3" data-testid={`assign-${a.id}`}>
                {a.is_primary && <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${navy ? "bg-[#E7F5FB] text-[#07539A] border-[#BBD8E7]" : "bg-[var(--copper)]/10 text-[var(--copper)] border-[var(--copper)]/30"}`}>Primary</span>}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] truncate">{a.partner_name}</div>
                  <div className="text-[10px] text-[var(--text-dim)]">Assigned {formatDate(a.assigned_at)} · Lock expires {formatDate(a.expires_at)}</div>
                </div>
                {a.commission_pct != null && (
                  <div className="text-right shrink-0">
                    <div className={`font-mono text-[13px] tabular-nums ${navy ? "text-[#07539A]" : "text-[var(--copper)]"}`}>{a.commission_pct}%</div>
                    <div className="text-[9px] uppercase tracking-widest text-[var(--text-dim)]">of payment</div>
                  </div>
                )}
              </div>
            ))}
            {active.some((a) => {
              try { return new Date(a.expires_at) < new Date(Date.now() + 30 * 86400000); }
              catch { return false; }
            }) && (
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[var(--warning)]">
                <AlertCircle size={10} /> Lock expires in &lt; 30 days — reassignable per Saarv rules
              </div>
            )}
          </div>
        )}
      </div>
      {assignOpen && canManage && (
        <AssignModal
          entityType={entityType}
          entityId={entityId}
          partners={partners}
          onClose={() => { setAssignOpen(false); load(); }}
        />
      )}
      {splitOpen && canSplit && (
        <SplitModal
          entityType={entityType}
          entityId={entityId}
          partners={partners}
          current={active}
          onClose={() => { setSplitOpen(false); load(); }}
        />
      )}
      {proposeOpen && canProposeTransfer && (
        <ProposeTransferModal entityType={entityType} entityId={entityId} partners={partners} currentPartnerId={user.partner_id}
          onClose={() => setProposeOpen(false)} />
      )}
    </div>
  );
}

function ProposeTransferModal({ entityType, entityId, partners, currentPartnerId, onClose }) {
  const options = partners.filter(partner => partner.id !== currentPartnerId);
  const [partnerId, setPartnerId] = useState(options[0]?.id || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!partnerId) return toast.error("Select a partner");
    setSaving(true);
    try {
      await api.post("/partner-assignments/proposals", {
        entity_type: entityType,
        entity_id: entityId,
        new_partner_id: partnerId,
        notes: notes.trim() || null,
      });
      toast.success("Transfer proposed · awaiting partner response");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not propose transfer");
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-[24px] border border-[#BBD8E7] bg-[#F3FAFD] shadow-2xl overflow-hidden" onClick={event => event.stopPropagation()} data-testid="propose-transfer-modal">
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3">
          <Users size={16} className="text-[#07539A]" />
          <div className="font-display text-[18px] text-[#12324A] flex-1">Propose partner transfer</div>
          <button type="button" onClick={onClose}><X size={15} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[11px] text-[#607D8B]">The customer stays with your firm until the proposed partner accepts.</p>
          <select value={partnerId} onChange={event => setPartnerId(event.target.value)} data-testid="propose-transfer-partner"
            className="w-full h-11 px-3 rounded-xl bg-white border border-[#C9DCE6] outline-none focus:border-[#07539A] text-[13px]">
            <option value="">Select partner…</option>
            {options.map(partner => <option key={partner.id} value={partner.id}>{partner.firm || partner.name}</option>)}
          </select>
          <textarea rows={3} value={notes} onChange={event => setNotes(event.target.value)} placeholder="Why are you proposing this transfer?" data-testid="propose-transfer-notes"
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#C9DCE6] outline-none focus:border-[#07539A] text-[13px] resize-none" />
          <button type="button" onClick={save} disabled={saving || !partnerId} data-testid="propose-transfer-submit"
            className="w-full h-11 rounded-xl bg-[#07539A] hover:bg-[#063F73] text-white text-[13px] font-medium disabled:opacity-40 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Send proposal
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Marketing: assign one primary partner (full commission pool by default). */
function AssignModal({ entityType, entityId, partners, onClose }) {
  const [partnerId, setPartnerId] = useState(partners[0]?.id || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPartnerId(partners[0]?.id || "");
  }, [partners]);

  async function save() {
    if (partners.length === 0) return toast.error("Create partner firms on the Partners page before assigning");
    if (!partnerId) return toast.error("Select a partner");
    setSaving(true);
    try {
      await api.post("/partner-assignments", {
        entity_type: entityType,
        entity_id: entityId,
        partners: [{ partner_id: partnerId, is_primary: true }],
        notes,
      });
      toast.success("Primary partner assigned · 6-month lock started");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]"><Users size={15} /></div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">Assign Primary Partner</div>
          <button type="button" onClick={onClose} data-testid="assign-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A]"><X size={15} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {partners.length === 0 ? (
            <div className="rounded-[16px] border border-[var(--warning)]/30 bg-white px-4 py-3 text-[12px] text-[var(--warning)]">
              No partner firms exist yet. Open the Partners page and create a partner firm first.
            </div>
          ) : null}
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] px-4 py-3 text-[11px] text-[#607D8B]">
            Select the ascribed partner for this customer. They receive the full commission pool (typically 30% of payment) until an admin splits it.
          </div>
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
            <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Partner firm</label>
            <select
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              data-testid="assign-partner-0"
              className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px]"
            >
              <option value="" disabled>Select partner firm…</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.firm ? ` — ${p.firm}` : ""}</option>
              ))}
            </select>
          </div>
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
            <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="assign-notes"
              placeholder="Why this partner?"
              className="w-full mt-1 px-3 py-2 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px] resize-none"
            />
          </div>
        </div>
        <div className="p-5 pt-0 shrink-0">
          <button
            type="button"
            onClick={save}
            disabled={saving || partners.length === 0 || !partnerId}
            data-testid="assign-save"
            className="w-full h-11 rounded-[14px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium disabled:opacity-40 flex items-center justify-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirm Assignment · 6-month lock
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Admin: split commission pool across partners (R-08). */
function SplitModal({ entityType, entityId, partners, current, onClose }) {
  const initial = buildSplitRows(current, partners);
  const [pool, setPool] = useState(initial.pool);
  const [rows, setRows] = useState(initial.rows);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = buildSplitRows(current, partners, pool);
    setPool(next.pool);
    setRows(next.rows);
  }, [current, partners]);

  const total = rows.reduce((s, r) => s + Number(r.commission_pct || 0), 0);

  function addRow() {
    if (rows.length >= 4) return;
    setRows([...rows, { partner_id: partners[0]?.id || "", commission_pct: 0, is_primary: false }]);
  }
  function removeRow(i) {
    setRows(rows.filter((_, idx) => idx !== i));
  }
  function updateRow(i, patch) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    if (patch.is_primary) next.forEach((r, idx) => { if (idx !== i) r.is_primary = false; });
    setRows(next);
  }

  async function save() {
    if (partners.length === 0) return toast.error("No partner firms available");
    if (rows.some((r) => !r.partner_id)) return toast.error("Select a partner for every row");
    if (Math.abs(total - Number(pool)) > 0.5) {
      return toast.error(`Shares must total ${pool}% of payment (currently ${total}%)`);
    }
    if (!rows.some((r) => r.is_primary)) return toast.error("Mark one partner as primary");
    setSaving(true);
    try {
      await api.post("/partner-assignments/commission-split", {
        entity_type: entityType,
        entity_id: entityId,
        commission_pool_pct: Number(pool),
        partners: rows.map((r) => ({
          partner_id: r.partner_id,
          commission_pct: Number(r.commission_pct),
          is_primary: r.is_primary,
        })),
        notes,
      });
      toast.success("Commission split saved");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Split failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-black/10 rounded-md shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="split-modal">
        <div className="p-3 border-b border-black/10 flex items-center gap-2 shrink-0">
          <Split size={13} className="text-[var(--copper)]" />
          <div className="font-display text-[14px] flex-1">Split Commission</div>
          <button type="button" onClick={onClose} data-testid="split-close"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="text-[11px] text-[var(--text-muted)]">
            Divide the commission pool across partners. Each share is a % of customer payment
            (e.g. 5% + 25% = 30% total pool).
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-[var(--text-dim)] shrink-0">Total pool</label>
            <input
              type="number"
              min="1"
              max="100"
              value={pool}
              onChange={(e) => setPool(e.target.value)}
              data-testid="split-pool"
              className="w-20 h-9 px-2 rounded-md bg-white border border-black/15 outline-none text-[13px] text-right font-mono"
            />
            <span className="text-[11px]">% of payment</span>
          </div>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap sm:flex-nowrap" data-testid={`split-row-${i}`}>
                <select
                  value={r.partner_id}
                  onChange={(e) => updateRow(i, { partner_id: e.target.value })}
                  data-testid={`split-partner-${i}`}
                  className="flex-1 min-w-0 w-full sm:w-auto h-9 px-2 rounded-md bg-white border border-black/15 outline-none text-[13px]"
                >
                  <option value="" disabled>Select partner…</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.firm ? ` — ${p.firm}` : ""}</option>
                  ))}
                </select>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={r.commission_pct}
                    onChange={(e) => updateRow(i, { commission_pct: e.target.value })}
                    data-testid={`split-pct-${i}`}
                    className="w-16 h-9 px-2 rounded-md bg-white border border-black/15 outline-none text-[13px] text-right font-mono"
                  />
                  <span className="text-[11px]">%</span>
                  <label className="flex items-center gap-1 text-[11px] whitespace-nowrap">
                    <input type="radio" checked={r.is_primary} onChange={() => updateRow(i, { is_primary: true })} data-testid={`split-primary-${i}`} />
                    Primary
                  </label>
                  {rows.length > 1 && (
                    <button type="button" onClick={() => removeRow(i)} data-testid={`split-remove-${i}`} className="text-[var(--text-dim)] hover:text-[var(--danger)]">
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {rows.length < 4 && partners.length > 0 && (
              <button type="button" onClick={addRow} data-testid="split-add-row" className="text-[11px] text-[var(--copper)] flex items-center gap-1">
                <Plus size={11} /> Add partner
              </button>
            )}
          </div>
          <div className={`text-[11px] font-mono ${Math.abs(total - Number(pool)) < 0.5 ? "text-[var(--success)]" : "text-[var(--danger)]"}`}>
            Allocated: {total}% {Math.abs(total - Number(pool)) < 0.5 ? "✓" : `(must equal ${pool}% pool)`}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="split-notes"
              placeholder="Reason for split — second partner relationship, etc."
              className="w-full mt-1 px-2 py-2 rounded-md bg-white border border-black/15 focus:border-[var(--copper)] outline-none text-[13px] resize-none"
            />
          </div>
        </div>
        <div className="p-4 border-t border-black/10 shrink-0">
          <button
            type="button"
            onClick={save}
            disabled={saving || partners.length === 0 || rows.some((r) => !r.partner_id) || Math.abs(total - Number(pool)) > 0.5}
            data-testid="split-save"
            className="w-full h-10 rounded-md bg-[var(--copper)] hover:bg-[var(--copper-strong)] text-white text-[13px] font-medium disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirm Commission Split
          </button>
        </div>
      </div>
    </div>
  );
}
